import { useState, useEffect, useCallback } from 'react';
import { UserPlus, Mail, Trash2, Clock, CheckCircle, XCircle, Send, Users, Shield, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

import { API_URL } from '@/config/api';

// AUTHORITATIVE BOH ROLE LIST - All invitable roles (super_admin cannot be invited)
const BOH_ROLES = [
  { 
    value: 'manager', 
    label: 'Manager', 
    description: 'All access EXCEPT system settings - orders, prep, payments, users, reports',
    color: 'bg-pink-100 text-pink-800 border-pink-200',
    permissions: ['orders', 'prep', 'stock', 'messages', 'reports', 'users']
  },
  { 
    value: 'operations_manager', 
    label: 'Operations Manager', 
    description: 'Operational control - orders, prep sheets, kitchen dashboards, stock (no finance)',
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    permissions: ['orders', 'prep', 'stock', 'kitchen_dashboard']
  },
  { 
    value: 'kitchen', 
    label: 'Kitchen Staff', 
    description: 'Production only - daily prep sheets, meal images, quantities (NO prices/payments)',
    color: 'bg-orange-100 text-orange-800 border-orange-200',
    permissions: ['prep']
  },
  { 
    value: 'customer_support', 
    label: 'Customer Support', 
    description: 'Customer interaction - messages, order references, food safety cases (no values)',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    permissions: ['messages', 'reviews', 'food_safety']
  },
  { 
    value: 'finance', 
    label: 'Finance', 
    description: 'Full financial visibility - orders (read), payments, costs, sales, invoices, audit logs',
    color: 'bg-green-100 text-green-800 border-green-200',
    permissions: ['orders_read', 'payments', 'costs', 'reports', 'audit', 'invoices']
  },
  { 
    value: 'driver', 
    label: 'Driver', 
    description: 'Delivery driver - view orders, verify collection codes, delivery addresses, Google Maps',
    color: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    permissions: ['orders', 'verify_collection', 'delivery']
  },
];

// All roles including super_admin for display purposes
const ALL_ROLES = {
  super_admin: { label: 'Super Admin', color: 'bg-red-100 text-red-800 border-red-200' },
  manager: { label: 'Manager', color: 'bg-pink-100 text-pink-800 border-pink-200' },
  operations_manager: { label: 'Operations Manager', color: 'bg-purple-100 text-purple-800 border-purple-200' },
  kitchen: { label: 'Kitchen Staff', color: 'bg-orange-100 text-orange-800 border-orange-200' },
  customer_support: { label: 'Customer Support', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  finance: { label: 'Finance', color: 'bg-green-100 text-green-800 border-green-200' },
  fulfilment: { label: 'Fulfilment', color: 'bg-cyan-100 text-cyan-800 border-cyan-200' },
};

// Role badge color mapping
const getRoleBadgeColor = (role) => {
  return ALL_ROLES[role]?.color || 'bg-gray-100 text-gray-800 border-gray-200';
};

const getRoleLabel = (role) => {
  return ALL_ROLES[role]?.label || role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
};

export default function AdminInvitations() {
  const { token, user } = useAuth();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    role: '',
    name: ''
  });

  const fetchInvitations = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/invitations`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to fetch invitations');
      
      const data = await response.json();
      setInvitations(data);
    } catch (error) {
      console.error('Error fetching invitations:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  const sendInvitation = async () => {
    if (!formData.email || !formData.role) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/users/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send invitation');
      }

      toast.success(`Invitation sent to ${formData.email}`);
      setDialogOpen(false);
      setFormData({ email: '', role: '', name: '' });
      fetchInvitations();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSending(false);
    }
  };

  const revokeInvitation = async (invitationId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to revoke invitation');

      toast.success('Invitation revoked');
      fetchInvitations();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-ZA', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="invitations-loading">
        <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  // Only super_admin can access this page
  if (user?.role !== 'super_admin') {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <XCircle className="h-12 w-12 text-red-400 mb-4" />
        <h2 className="text-xl font-semibold text-slate-800 mb-2">Access Denied</h2>
        <p className="text-slate-500">Only Super Admins can manage BOH invitations.</p>
      </div>
    );
  }

  const pendingInvitations = invitations.filter(i => !i.is_used && !isExpired(i.expires_at));
  const expiredInvitations = invitations.filter(i => !i.is_used && isExpired(i.expires_at));
  const usedInvitations = invitations.filter(i => i.is_used);

  return (
    <div data-testid="admin-invitations">
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-slate-900 flex items-center gap-3">
            <UserPlus className="h-8 w-8 text-amber-500" />
            BOH Staff Invitations
          </h1>
          <p className="text-slate-500 mt-1">
            Invite new staff members to the Back-of-House portal with specific roles
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="send-invite-btn">
              <Send className="h-4 w-4 mr-2" />
              Invite Staff Member
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg" aria-describedby="invite-dialog-description">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-amber-500" />
                Invite BOH User
              </DialogTitle>
              <p id="invite-dialog-description" className="text-sm text-slate-500">
                Send an invitation to add a new staff member to the BOH portal.
              </p>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="staff@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData(f => ({ ...f, email: e.target.value }))}
                  data-testid="invite-email-input"
                />
              </div>
              <div>
                <Label htmlFor="name">Full Name (Optional)</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))}
                  data-testid="invite-name-input"
                />
              </div>
              <div>
                <Label htmlFor="role" className="flex items-center gap-2">
                  Role * 
                  <span className="text-xs text-slate-500">(determines access permissions)</span>
                </Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(value) => setFormData(f => ({ ...f, role: value }))}
                >
                  <SelectTrigger data-testid="invite-role-select" className="h-auto min-h-[44px]">
                    <SelectValue placeholder="Select a role..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-[400px]">
                    {BOH_ROLES.map(role => (
                      <SelectItem key={role.value} value={role.value} className="py-3">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <Badge className={`${role.color} text-xs`}>{role.label}</Badge>
                          </div>
                          <span className="text-xs text-slate-500 whitespace-normal">
                            {role.description}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Role Permissions Info Box */}
              {formData.role && (
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-start gap-2">
                    <Info className="h-4 w-4 text-slate-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-slate-700">
                        {getRoleLabel(formData.role)} Permissions:
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        {BOH_ROLES.find(r => r.value === formData.role)?.description}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={sendInvitation} 
                disabled={sending || !formData.role} 
                data-testid="confirm-invite-btn"
              >
                {sending ? 'Sending...' : 'Send Invitation'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Role Legend */}
      <Card className="mb-6 bg-slate-50">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-4 w-4 text-slate-600" />
            Role Permissions Reference
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {BOH_ROLES.filter(r => r.value !== 'super_admin').map(role => (
              <div key={role.value} className="flex items-start gap-2 p-2 bg-white rounded border">
                <Badge className={`${role.color} text-xs shrink-0`}>{role.label}</Badge>
                <span className="text-xs text-slate-500 leading-tight">{role.description}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-3 bg-amber-100 rounded-lg">
              <Clock className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-800">{pendingInvitations.length}</p>
              <p className="text-sm text-amber-600">Pending</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-800">{usedInvitations.length}</p>
              <p className="text-sm text-green-600">Accepted</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-3 bg-slate-100 rounded-lg">
              <XCircle className="h-6 w-6 text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{expiredInvitations.length}</p>
              <p className="text-sm text-slate-600">Expired</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-500" />
              Pending Invitations
            </CardTitle>
            <CardDescription>These invitations are waiting to be accepted</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingInvitations.map((invitation) => (
                <div 
                  key={invitation.invitation_id} 
                  className="flex items-center justify-between p-4 bg-amber-50/50 rounded-lg border border-amber-100"
                  data-testid={`invitation-${invitation.invitation_id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-amber-100 rounded-full">
                      <Mail className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">{invitation.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={getRoleBadgeColor(invitation.role)}>
                          {getRoleLabel(invitation.role)}
                        </Badge>
                        <span className="text-xs text-slate-500">
                          Sent {formatDate(invitation.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">
                      Expires {formatDate(invitation.expires_at)}
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => revokeInvitation(invitation.invitation_id)}
                      data-testid={`revoke-${invitation.invitation_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Accepted Invitations */}
      {usedInvitations.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              Accepted Invitations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {usedInvitations.map((invitation) => (
                <div 
                  key={invitation.invitation_id} 
                  className="flex items-center justify-between p-4 bg-green-50/50 rounded-lg border border-green-100"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-green-100 rounded-full">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">{invitation.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={getRoleBadgeColor(invitation.role)}>
                          {getRoleLabel(invitation.role)}
                        </Badge>
                        <span className="text-xs text-green-600">
                          Accepted
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {invitations.length === 0 && (
        <Card className="bg-slate-50 border-dashed">
          <CardContent className="p-12 text-center">
            <Users className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-600 mb-2">No Invitations Yet</h3>
            <p className="text-slate-500 mb-4">Start building your BOH team by sending invitations.</p>
            <Button onClick={() => setDialogOpen(true)}>
              <Send className="h-4 w-4 mr-2" />
              Send First Invitation
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
